import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Clock } from 'lucide-react';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { PickupScheduleFields } from '../../../../../components/warehouses/PickupScheduleFields';
import { updateWarehousePickupSchedule } from '../../../../../features/sellers/sellerDetailsThunks';
import {
  buildPickupSchedulePayload,
  describePickupSchedule,
  pickupFormFromWarehouse,
  validatePickupSchedule,
} from '../../../../../features/sellers/sellerFormUtils';

const NO_SCHEDULE = 'Sin horario';

/**
 * The hours a courier may collect at one warehouse, from the legacy
 * `CompanyDetail`.
 *
 * It matters more than it looks: Samex reads these windows when the pickup is
 * requested and falls back to 09:00–18:00 when they are empty, so a warehouse
 * created before the wizard existed is collecting on hours nobody chose, and
 * until now there was no way to fix that outside the legacy.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object|null} props.warehouse - As the backend answers it.
 */
export const PickupScheduleModal = ({ isOpen, onClose, warehouse }) => {
  const dispatch = useDispatch();
  const savingId = useSelector((state) => state.sellerDetails.warehouseScheduleSavingId);

  const [form, setForm] = useState(() => pickupFormFromWarehouse(warehouse));
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState(null);

  // Seeded on each opening, from the warehouse the row handed over.
  useEffect(() => {
    if (!isOpen) return;
    setForm(pickupFormFromWarehouse(warehouse));
    setErrors({});
    setSaveError(null);
  }, [isOpen, warehouse]);

  const saving = savingId === warehouse?.id;
  const hasNoSchedule = describePickupSchedule(warehouse) === NO_SCHEDULE;

  const handleSave = async () => {
    const nextErrors = validatePickupSchedule(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSaveError(null);
    const action = await dispatch(updateWarehousePickupSchedule({
      warehouseId: warehouse.id,
      schedule: buildPickupSchedulePayload(form),
    }));

    if (action.meta.requestStatus === 'fulfilled') onClose();
    else setSaveError(action.payload);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={`Horario de retiro${warehouse ? ` — ${warehouse.name}` : ''}`}
      testId="warehouse-schedule-modal"
      busy={saving}
    >
      <div className="space-y-5">
        {hasNoSchedule && (
          <div
            data-testid="warehouse-schedule-empty"
            className="bg-blue-50 dark:bg-blue-900/20 text-info p-3 rounded-xl border border-blue-200 dark:border-blue-900 text-sm"
          >
            Esta bodega no tiene horario guardado, así que el courier pasa en el suyo por defecto, de
            09:00 a 18:00. Al guardar, queda elegido por ustedes.
          </div>
        )}

        {saveError && (
          <div
            data-testid="warehouse-schedule-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm"
          >
            {saveError}
          </div>
        )}

        <PickupScheduleFields
          idPrefix="warehouse-schedule"
          allDay={form.allDay}
          amFrom={form.amFrom}
          amTo={form.amTo}
          pmFrom={form.pmFrom}
          pmTo={form.pmTo}
          onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
          errors={errors}
          disabled={saving}
        />

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            icon={Clock}
            loading={saving}
            loadingLabel="Guardando..."
            onClick={handleSave}
            data-testid="warehouse-schedule-save"
          >
            Guardar horario
          </Button>
        </div>
      </div>
    </Modal>
  );
};
